/**
 * Leg Route Audit
 *
 * Review legs within a time range grouped by Route.
 * Designed for incident response / data validation after route update bugs.
 *
 * Uses read-only API endpoints:
 * - GET /reports/audit/legs-by-route/summary
 * - GET /reports/audit/legs-by-route/details
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { SidebarContainer, StatusBadge } from "../../components";
import LocationDropdown from "../../components/LocationDropdown";
import {
  Card,
  Button,
  DateRangePicker,
  Modal,
  Select,
  Alert,
  Input,
  Checkbox,
} from "../../shared";
import { Table } from "../../shared/Table";
import { useAuth } from "../../hooks/useAuth";
import { useUpdateLegMutation } from "../../redux/services/legs/legsApi";
import { useGetDistanceMutation } from "../../redux/services/map/mapApi";
import {
  useGetLegsByRouteAuditSummaryQuery,
  useLazyGetLegsByRouteAuditDetailsQuery,
  useGetSavedReportQuery,
  useSaveReportMutation,
} from "../../redux/services/reports/reportsApi";
import { getMilesFromMeters } from "../Jobs/jobs.utils";

const LEG_AUDIT_REPORT_STORAGE_KEY = "leg-route-audit-report-state-v1";

const DATE_FIELD_OPTIONS = [
  { label: "Updated (dateUpdated)", value: "dateUpdated" },
  { label: "Created (dateCreated)", value: "dateCreated" },
  { label: "Appointment (appointmentDate)", value: "appointmentDate" },
  { label: "Start (startDate)", value: "startDate" },
  { label: "End (endDate)", value: "endDate" },
];

const safeFormat = (value) => {
  if (!value) return "";
  try {
    return format(new Date(value), "yyyy-MM-dd HH:mm");
  } catch (e) {
    return String(value);
  }
};

const getRouteLabel = (row) => {
  const origin = row?.route?.origin?.name || "Unknown origin";
  const dest = row?.route?.destination?.name || "Unknown destination";
  return `${origin} → ${dest}`;
};

/**
 * Human-readable job label (matches jobs list / reports tables).
 * @param {object} job - populated Job doc or null
 * @returns {string}
 */
const getJobHumanLabel = (job) => {
  if (!job || typeof job !== "object") return "—";
  return job.loadNumber || job.orderNumber || job.jobId || "—";
};

/**
 * **Leg** order number (field on the Leg model only — not the job’s load number).
 * @param {object} leg
 * @returns {string}
 */
const getLegOrderNumberLabel = (leg) => {
  if (leg?.orderNumber != null && String(leg.orderNumber).trim() !== "") {
    return String(leg.orderNumber).trim();
  }
  return "—";
};

const normalizeId = (value) => {
  if (!value) return "";
  // bson ObjectId usually has toString()
  if (typeof value?.toString === "function") return value.toString();
  // Sometimes we get nested objects (defensive)
  if (value?._id && typeof value._id?.toString === "function") return value._id.toString();
  return String(value);
};

const toNumberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const getLocationAddress = (location) =>
  location?.address?.fullAddress || location?.name || "";

const readPersistedReportState = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(LEG_AUDIT_REPORT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (error) {
    return null;
  }
};

const LegRouteAudit = ({
  embedded = false,
  savedReportIdToLoad = null,
  savedReportRequestToken = null,
  onSavedReportApplied,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const persistedState = readPersistedReportState();
  const { user } = useAuth();
  const [dateField, setDateField] = useState(
    persistedState?.dateField || "dateUpdated"
  );
  const [dateRange, setDateRange] = useState(() => {
    const persistedStart = persistedState?.dateRange?.startDate
      ? new Date(persistedState.dateRange.startDate)
      : null;
    const persistedEnd = persistedState?.dateRange?.endDate
      ? new Date(persistedState.dateRange.endDate)
      : null;
    if (persistedStart && persistedEnd) {
      return { startDate: persistedStart, endDate: persistedEnd };
    }
    const end = new Date();
    const start = new Date(end.getTime() - 1000 * 60 * 60 * 24 * 7);
    return { startDate: start, endDate: end };
  });
  const [onlyDuplicates, setOnlyDuplicates] = useState(
    persistedState?.onlyDuplicates ?? true
  );
  const [minLegCount, setMinLegCount] = useState(persistedState?.minLegCount ?? 2);
  /** Exclude route groups where origin or destination name contains any of these (comma-separated). */
  const [excludeLocationSubstrings, setExcludeLocationSubstrings] = useState(
    persistedState?.excludeLocationSubstrings ?? "CANTEX YARD"
  );
  const [useExcludeLocationSubstrings, setUseExcludeLocationSubstrings] = useState(
    persistedState?.useExcludeLocationSubstrings ?? true
  );

  const [selectedRoute, setSelectedRoute] = useState(persistedState?.selectedRoute || null);
  const [showDetails, setShowDetails] = useState(persistedState?.showDetails || false);
  const [showOnlyMissingContext, setShowOnlyMissingContext] = useState(
    persistedState?.showOnlyMissingContext || false
  );
  const [lastDetailsRequest, setLastDetailsRequest] = useState(
    persistedState?.lastDetailsRequest || null
  );
  const [persistedUpdatedLegs, setPersistedUpdatedLegs] = useState(
    persistedState?.persistedUpdatedLegs || {}
  );
  const [activeSavedReportContext, setActiveSavedReportContext] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveDraft, setSaveDraft] = useState({
    name: "",
    description: "",
  });
  const [editingLeg, setEditingLeg] = useState(null);
  const [editForm, setEditForm] = useState({
    origin: null,
    destination: null,
    mileageUsed: "",
  });
  const lastCalculatedRouteRef = useRef(null);

  const summaryParams = useMemo(() => {
    const start = dateRange?.startDate ? new Date(dateRange.startDate) : null;
    const end = dateRange?.endDate ? new Date(dateRange.endDate) : null;
    if (!start || !end) return null;
    const trimmed = String(excludeLocationSubstrings || "").trim();
    return {
      start: start.toISOString(),
      end: end.toISOString(),
      dateField,
      limit: 500,
      ...(useExcludeLocationSubstrings && trimmed
        ? { excludeLocationSubstrings: trimmed }
        : {}),
    };
  }, [dateRange, dateField, useExcludeLocationSubstrings, excludeLocationSubstrings]);

  const {
    data: summaryResponse,
    isFetching: isFetchingSummary,
    error: summaryError,
    refetch: refetchSummary,
  } = useGetLegsByRouteAuditSummaryQuery(summaryParams, {
    skip: !summaryParams,
  });

  const [triggerDetails, detailsState] = useLazyGetLegsByRouteAuditDetailsQuery();
  const [updateLeg, { isLoading: isUpdatingLeg }] = useUpdateLegMutation();
  const [getDistance, { isLoading: isCalculatingDistance }] = useGetDistanceMutation();
  const [saveReport, { isLoading: isSavingReport }] = useSaveReportMutation();

  const urlSavedReportId = searchParams.get("savedReportId");
  const effectiveSavedReportId = savedReportIdToLoad || urlSavedReportId;
  const { data: savedReportData } = useGetSavedReportQuery(effectiveSavedReportId, {
    skip: !effectiveSavedReportId,
  });
  const appliedSavedReportRef = useRef({ id: null, token: null });

  const summaryRows = summaryResponse?.data || [];
  const filteredSummaryRows = useMemo(() => {
    const min = Number(minLegCount);
    const minCount = Number.isFinite(min) && min > 0 ? min : 1;
    return summaryRows.filter((row) => {
      const count = Number(row?.count || 0);
      if (onlyDuplicates && count < 2) return false;
      return count >= minCount;
    });
  }, [summaryRows, onlyDuplicates, minLegCount]);

  const summaryColumns = useMemo(
    () => [
      {
        header: "Route",
        accessorKey: "routeId",
        cell: ({ row }) => {
          const item = row?.original;
          return (
            <div className="flex flex-col">
              <div className="font-medium">{getRouteLabel(item)}</div>
              <div className="text-xs text-gray-500">
                Route ID: {String(item?.routeId || "")}
              </div>
            </div>
          );
        },
      },
      {
        header: "Legs",
        accessorKey: "count",
        cell: ({ row }) => {
          const item = row?.original;
          return <div className="font-medium">{item?.count || 0}</div>;
        },
      },
      {
        header: "Mileage",
        accessorKey: "route.mileage",
        cell: ({ row }) => {
          const mileage = row?.original?.route?.mileage;
          return mileage != null ? Number(mileage).toFixed(2) : "";
        },
      },
      {
        header: "First seen",
        accessorKey: "firstSeenAt",
        cell: ({ row }) => safeFormat(row?.original?.firstSeenAt),
      },
      {
        header: "Last seen",
        accessorKey: "lastSeenAt",
        cell: ({ row }) => safeFormat(row?.original?.lastSeenAt),
      },
      {
        header: "",
        id: "actions",
        cell: ({ row }) => {
          const item = row?.original;
          return (
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={async () => {
                  setSelectedRoute(item);
                  setShowDetails(true);
                  const request = {
                    routeId: normalizeId(item?.routeId),
                    start: summaryParams?.start,
                    end: summaryParams?.end,
                    dateField,
                    limit: 2000,
                  };
                  setLastDetailsRequest(request);
                  await triggerDetails(request);
                }}
              >
                View legs
              </Button>
            </div>
          );
        },
      },
    ],
    [dateField, summaryParams, triggerDetails]
  );

  const legs = detailsState?.data?.data || [];
  const filteredLegs = useMemo(() => {
    if (!showOnlyMissingContext) return legs;
    return legs.filter((leg) => {
      const hasManifest = !!leg?.manifestId?._id || !!leg?.manifestId;
      const hasInvoice = !!leg?.jobId?.invoiceId?._id || !!leg?.jobId?.invoiceId;
      return !hasManifest || !hasInvoice;
    });
  }, [legs, showOnlyMissingContext]);

  const selectedRouteId = normalizeId(selectedRoute?.routeId);
  const persistedLegRowsForRoute = useMemo(() => {
    return Object.values(persistedUpdatedLegs || {}).filter(
      (leg) => normalizeId(leg?.__audit?.contextRouteId) === selectedRouteId
    );
  }, [persistedUpdatedLegs, selectedRouteId]);

  const displayedLegs = useMemo(() => {
    const merged = new Map();
    filteredLegs.forEach((leg) => merged.set(normalizeId(leg?._id), leg));
    persistedLegRowsForRoute.forEach((leg) => {
      const legId = normalizeId(leg?._id);
      const existing = merged.get(legId);
      merged.set(legId, {
        ...(existing || {}),
        ...leg,
        __audit: {
          ...(existing?.__audit || {}),
          ...(leg?.__audit || {}),
          updated: true,
        },
      });
    });
    return Array.from(merged.values());
  }, [filteredLegs, persistedLegRowsForRoute]);

  // Show every row on one table "page" (shared Table's "View All" link is for list pages and breaks without `id`)
  const summaryTablePageSize = useMemo(
    () => Math.min(Math.max(filteredSummaryRows.length, 1), 5000),
    [filteredSummaryRows.length]
  );
  const detailsTablePageSize = useMemo(
    () => Math.min(Math.max(displayedLegs.length, 1), 5000),
    [displayedLegs.length]
  );

  const legColumns = useMemo(
    () => [
      {
        header: "Leg order #",
        id: "legOrderNumber",
        accessorKey: "orderNumber",
        cell: ({ row }) => {
          const leg = row?.original;
          const jobRef = leg?.jobId?._id || leg?.jobId;
          const text = getLegOrderNumberLabel(leg);
          if (jobRef) {
            return (
              <Link
                to={`/jobs/${jobRef}`}
                className="text-primary-600 hover:text-primary-800 hover:underline font-medium"
                onClick={(e) => e.stopPropagation()}
              >
                {text}
              </Link>
            );
          }
          return <span className="text-gray-900">{text}</span>;
        },
      },
      {
        header: "Job",
        accessorKey: "jobId.loadNumber",
        cell: ({ row }) => {
          const leg = row?.original;
          const job = leg?.jobId;
          const id = job?._id || job;
          const text = getJobHumanLabel(job);
          if (id) {
            return (
              <Link
                to={`/jobs/${id}`}
                className="text-primary-600 hover:text-primary-800 hover:underline font-medium"
                onClick={(e) => e.stopPropagation()}
              >
                {text}
              </Link>
            );
          }
          return <span>—</span>;
        },
      },
      {
        header: "Manifest",
        accessorKey: "manifestId.manifestId",
        cell: ({ row }) => {
          const leg = row?.original;
          const m = leg?.manifestId;
          const id = m?._id || m;
          const human = m?.manifestId;
          if (!id) return <span className="text-gray-500">—</span>;
          return (
            <Link
              to={`/manifests/${id}`}
              className="text-primary-600 hover:text-primary-800 hover:underline font-medium"
              onClick={(e) => e.stopPropagation()}
            >
              {human ? String(human) : "Manifest"}
            </Link>
          );
        },
      },
      {
        header: "Invoice",
        accessorKey: "jobId.invoiceId.invoiceId",
        cell: ({ row }) => {
          const leg = row?.original;
          const inv = leg?.jobId?.invoiceId;
          const id = inv?._id || inv;
          const human = inv?.invoiceId;
          if (!id) return <span className="text-gray-500">—</span>;
          return (
            <Link
              to={`/invoices/${id}`}
              className="text-primary-600 hover:text-primary-800 hover:underline font-medium"
              onClick={(e) => e.stopPropagation()}
            >
              {human ? String(human) : "Invoice"}
            </Link>
          );
        },
      },
      {
        header: "Client",
        accessorKey: "client.name",
        cell: ({ row }) => row?.original?.client?.name || "",
      },
      {
        header: "Driver",
        accessorKey: "driver.fullName",
        cell: ({ row }) => row?.original?.driver?.fullName || "",
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => (
          <StatusBadge status={row?.original?.status} component="leg" size="sm" />
        ),
      },
      {
        header: "Appointment",
        accessorKey: "appointmentDate",
        cell: ({ row }) => safeFormat(row?.original?.appointmentDate),
      },
      {
        header: "Updated",
        accessorKey: "dateUpdated",
        cell: ({ row }) => safeFormat(row?.original?.dateUpdated),
      },
      {
        header: "Audit",
        id: "auditState",
        cell: ({ row }) => {
          const isUpdatedInReport = !!row?.original?.__audit?.updated;
          if (!isUpdatedInReport) return <span className="text-gray-400">—</span>;
          return (
            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
              Updated
            </span>
          );
        },
      },
      {
        header: "Mileage used",
        accessorKey: "mileageUsed",
        cell: ({ row }) => {
          const v = row?.original?.mileageUsed;
          return v != null ? Number(v).toFixed(2) : "";
        },
      },
      {
        header: "Route",
        accessorKey: "route._id",
        cell: ({ row }) => getRouteLabel(row?.original),
      },
      {
        header: "",
        id: "legActions",
        cell: ({ row }) => {
          const leg = row?.original;
          return (
            <div className="flex justify-end">
              <Button
                size="sm"
                variant="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingLeg(leg);
                  lastCalculatedRouteRef.current = null;
                  setEditForm({
                    origin: leg?.route?.origin || null,
                    destination: leg?.route?.destination || null,
                    mileageUsed:
                      leg?.mileageUsed != null
                        ? String(leg.mileageUsed)
                        : leg?.route?.mileage != null
                          ? String(leg.route.mileage)
                          : "",
                  });
                }}
              >
                Update leg
              </Button>
            </div>
          );
        },
      },
    ],
    []
  );

  const closeEditModal = () => {
    setEditingLeg(null);
    lastCalculatedRouteRef.current = null;
    setEditForm({
      origin: null,
      destination: null,
      mileageUsed: "",
    });
  };

  useEffect(() => {
    if (!editingLeg?._id) return;

    const originId = normalizeId(editForm?.origin?._id || editForm?.origin);
    const destinationId = normalizeId(editForm?.destination?._id || editForm?.destination);
    if (!originId || !destinationId) return;

    const currentOriginId = normalizeId(
      editingLeg?.route?.origin?._id || editingLeg?.route?.origin
    );
    const currentDestinationId = normalizeId(
      editingLeg?.route?.destination?._id || editingLeg?.route?.destination
    );
    const routeChanged = originId !== currentOriginId || destinationId !== currentDestinationId;
    if (!routeChanged) return;

    const routeKey = `${originId}-${destinationId}`;
    if (lastCalculatedRouteRef.current === routeKey) return;

    (async () => {
      try {
        const response = await getDistance({
          origin: {
            _id: originId,
            address: getLocationAddress(editForm?.origin),
          },
          destination: {
            _id: destinationId,
            address: getLocationAddress(editForm?.destination),
          },
        }).unwrap();

        if (response?.status === "success" && response?.data?.distance?.value != null) {
          const computedMiles = getMilesFromMeters(response.data.distance.value);
          setEditForm((prev) => ({
            ...prev,
            mileageUsed:
              computedMiles != null && Number.isFinite(Number(computedMiles))
                ? String(computedMiles)
                : prev.mileageUsed,
          }));
          lastCalculatedRouteRef.current = routeKey;
        }
      } catch (error) {
        // Keep manual editing available even if distance provider errors.
        console.error("Audit leg distance calc failed:", error);
      }
    })();
  }, [
    editingLeg?._id,
    editForm?.origin?._id,
    editForm?.destination?._id,
    getDistance,
    editingLeg?.route?.origin,
    editingLeg?.route?.destination,
  ]);

  const refreshDetails = async () => {
    if (!selectedRoute?.routeId) return;
    const request = {
      routeId: normalizeId(selectedRoute.routeId),
      start: summaryParams?.start,
      end: summaryParams?.end,
      dateField,
      limit: 2000,
    };
    setLastDetailsRequest(request);
    await triggerDetails(request);
  };

  useEffect(() => {
    if (!effectiveSavedReportId || !savedReportData) return;
    const alreadyApplied =
      appliedSavedReportRef.current?.id === effectiveSavedReportId &&
      appliedSavedReportRef.current?.token === savedReportRequestToken;
    if (alreadyApplied) return;

    const savedPayload = savedReportData?.data || savedReportData;
    const cfg = savedPayload?.queryParameters?.auditConfig;
    if (!cfg) return;

    if (cfg.dateField) setDateField(cfg.dateField);
    if (cfg.dateRange?.startDate || cfg.dateRange?.endDate) {
      setDateRange({
        startDate: cfg.dateRange?.startDate ? new Date(cfg.dateRange.startDate) : null,
        endDate: cfg.dateRange?.endDate ? new Date(cfg.dateRange.endDate) : null,
      });
    }
    if (typeof cfg.onlyDuplicates === "boolean") setOnlyDuplicates(cfg.onlyDuplicates);
    if (cfg.minLegCount != null) setMinLegCount(cfg.minLegCount);
    if (typeof cfg.excludeLocationSubstrings === "string") {
      setExcludeLocationSubstrings(cfg.excludeLocationSubstrings);
    }
    if (typeof cfg.useExcludeLocationSubstrings === "boolean") {
      setUseExcludeLocationSubstrings(cfg.useExcludeLocationSubstrings);
    }
    if (cfg.selectedRoute) {
      setSelectedRoute(cfg.selectedRoute);
      setShowDetails(true);

      // Deterministic load: trigger details immediately from saved config instead of
      // relying on follow-up state effects/timing.
      const savedStart = cfg?.dateRange?.startDate || null;
      const savedEnd = cfg?.dateRange?.endDate || null;
      if (savedStart && savedEnd) {
        const detailsRequest = {
          routeId: normalizeId(cfg.selectedRoute?.routeId),
          start: new Date(savedStart).toISOString(),
          end: new Date(savedEnd).toISOString(),
          dateField: cfg?.dateField || "dateUpdated",
          limit: 2000,
        };
        setLastDetailsRequest(detailsRequest);
        triggerDetails(detailsRequest);
      }
    }
    if (typeof cfg.showOnlyMissingContext === "boolean") {
      setShowOnlyMissingContext(cfg.showOnlyMissingContext);
    }
    if (cfg.persistedUpdatedLegs && typeof cfg.persistedUpdatedLegs === "object") {
      setPersistedUpdatedLegs(cfg.persistedUpdatedLegs);
    }
    setActiveSavedReportContext({
      id: effectiveSavedReportId,
      name:
        savedPayload?.name ||
        savedPayload?.reportName ||
        "Saved Leg Route Audit",
      description: savedPayload?.description || "",
      loadedAt: new Date().toISOString(),
    });

    appliedSavedReportRef.current = {
      id: effectiveSavedReportId,
      token: savedReportRequestToken,
    };

    if (urlSavedReportId) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("savedReportId");
      setSearchParams(nextParams, { replace: true });
    }

    onSavedReportApplied?.(effectiveSavedReportId);
  }, [
    savedReportData,
    effectiveSavedReportId,
    savedReportRequestToken,
    searchParams,
    setSearchParams,
    onSavedReportApplied,
    urlSavedReportId,
  ]);

  useEffect(() => {
    if (!showDetails || !selectedRoute?.routeId || !summaryParams) return;
    refreshDetails();
    // restore details view after reload with persisted selected route
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDetails, selectedRoute?.routeId, summaryParams?.start, summaryParams?.end, dateField]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const payload = {
      dateField,
      dateRange: {
        startDate: dateRange?.startDate ? new Date(dateRange.startDate).toISOString() : null,
        endDate: dateRange?.endDate ? new Date(dateRange.endDate).toISOString() : null,
      },
      onlyDuplicates,
      minLegCount,
      excludeLocationSubstrings,
      useExcludeLocationSubstrings,
      selectedRoute,
      showDetails,
      showOnlyMissingContext,
      lastDetailsRequest,
      persistedUpdatedLegs,
    };
    localStorage.setItem(LEG_AUDIT_REPORT_STORAGE_KEY, JSON.stringify(payload));
  }, [
    dateField,
    dateRange?.startDate,
    dateRange?.endDate,
    onlyDuplicates,
    minLegCount,
    excludeLocationSubstrings,
    useExcludeLocationSubstrings,
    selectedRoute,
    showDetails,
    showOnlyMissingContext,
    lastDetailsRequest,
    persistedUpdatedLegs,
  ]);

  const handleSaveLegUpdate = async () => {
    if (!editingLeg?._id) return;

    const currentOriginId = normalizeId(editingLeg?.route?.origin?._id || editingLeg?.route?.origin);
    const currentDestinationId = normalizeId(
      editingLeg?.route?.destination?._id || editingLeg?.route?.destination
    );
    const nextOriginId = normalizeId(editForm?.origin?._id || editForm?.origin);
    const nextDestinationId = normalizeId(editForm?.destination?._id || editForm?.destination);

    const routeChanges = {};
    if (nextOriginId && nextOriginId !== currentOriginId) routeChanges.origin = nextOriginId;
    if (nextDestinationId && nextDestinationId !== currentDestinationId) {
      routeChanges.destination = nextDestinationId;
    }

    const currentMileage = toNumberOrNull(editingLeg?.mileageUsed);
    const nextMileage = toNumberOrNull(editForm?.mileageUsed);
    const mileageChanged = nextMileage != null && nextMileage !== currentMileage;

    if (!Object.keys(routeChanges).length && !mileageChanged) {
      toast.info("No changes to save.");
      return;
    }

    const payload = {
      parent: user?.parentCompany,
      legId: editingLeg._id,
      jobId: normalizeId(editingLeg?.jobId?._id || editingLeg?.jobId),
      data: {},
    };

    if (Object.keys(routeChanges).length) {
      payload.data.route = {
        _id: normalizeId(editingLeg?.route?._id),
        ...routeChanges,
      };
    }

    if (mileageChanged) {
      payload.data.mileageUsed = nextMileage;
      payload.data.totalMiles = nextMileage;
      payload.data.customMileage = nextMileage;
      payload.data.billedMiles = nextMileage;
      payload.data.useCustomMileage = true;
      payload.data.mileageSelectionType = "customInput";
    }

    try {
      const result = await updateLeg(payload).unwrap();
      const updatedLegData = result?.data || {};
      const updatedRoute = result?.data?.route || payload?.data?.route || editingLeg?.route;
      const updatedMileageValue =
        payload?.data?.mileageUsed != null
          ? payload.data.mileageUsed
          : updatedLegData?.mileageUsed ?? editingLeg?.mileageUsed;

      const persistedLegRow = {
        ...editingLeg,
        ...updatedLegData,
        route: {
          ...(editingLeg?.route || {}),
          ...(updatedRoute || {}),
          origin: editForm?.origin || updatedRoute?.origin || editingLeg?.route?.origin,
          destination:
            editForm?.destination ||
            updatedRoute?.destination ||
            editingLeg?.route?.destination,
        },
        mileageUsed: updatedMileageValue,
        dateUpdated: updatedLegData?.dateUpdated || new Date().toISOString(),
        __audit: {
          updated: true,
          updatedAt: new Date().toISOString(),
          contextRouteId: selectedRouteId,
          message: "Updated in this report view",
        },
      };
      setPersistedUpdatedLegs((prev) => ({
        ...(prev || {}),
        [normalizeId(editingLeg?._id)]: persistedLegRow,
      }));
      toast.success("Leg updated.");
      closeEditModal();
      refetchSummary();
      await refreshDetails();
    } catch (error) {
      toast.error(
        error?.data?.message || error?.error || "Failed to update leg. Please try again."
      );
    }
  };

  const handleSaveAuditReport = async () => {
    const reportName = String(saveDraft?.name || "").trim();
    if (!reportName) {
      toast.error("Report name is required.");
      return;
    }

    const payload = {
      reportName,
      modelName: "Leg",
      description:
        String(saveDraft?.description || "").trim() ||
        "Leg route audit report configuration",
      queryParameters: {
        reportType: "legRouteAudit",
        auditConfig: {
          dateField,
          dateRange: {
            startDate: dateRange?.startDate
              ? new Date(dateRange.startDate).toISOString()
              : null,
            endDate: dateRange?.endDate
              ? new Date(dateRange.endDate).toISOString()
              : null,
          },
          onlyDuplicates,
          minLegCount,
          excludeLocationSubstrings,
          useExcludeLocationSubstrings,
          selectedRoute,
          showOnlyMissingContext,
          persistedUpdatedLegs,
        },
      },
      reportData: null,
      reportMetadata: {
        reportType: "legRouteAudit",
      },
      isQueryBased: true,
    };

    try {
      await saveReport(payload).unwrap();
      toast.success("Audit report saved.");
      setShowSaveModal(false);
    } catch (error) {
      toast.error(error?.data?.message || "Failed to save audit report.");
    }
  };

  const header = () => (
    <div className="flex items-center justify-between w-full">
      <div className="flex flex-col">
        <div className="text-lg font-semibold">Leg Route Audit</div>
        <div className="text-xs text-gray-500">
          Review legs by route within a date window (incident validation).
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          variant="secondary"
          onClick={() => {
            setSaveDraft({
              name: `Leg Route Audit ${format(new Date(), "yyyy-MM-dd HH:mm")}`,
              description: "",
            });
            setShowSaveModal(true);
          }}
        >
          Save report
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            refetchSummary();
          }}
          loading={isFetchingSummary}
        >
          Refresh
        </Button>
      </div>
    </div>
  );

  const renderAuditContent = (parentHeight) => (
    <div
      className="px-3 pb-3 overflow-y-auto w-full"
      style={parentHeight ? { height: parentHeight } : undefined}
    >
      <div className="my-3 space-y-3">
        {activeSavedReportContext ? (
          <Alert type="info" title="Viewing saved report">
            <div className="text-xs text-gray-700 space-y-1">
              <div>
                <span className="font-medium">Name:</span> {activeSavedReportContext.name}
              </div>
              <div>
                <span className="font-medium">Saved report id:</span>{" "}
                {activeSavedReportContext.id}
              </div>
              {activeSavedReportContext.description ? (
                <div>
                  <span className="font-medium">Description:</span>{" "}
                  {activeSavedReportContext.description}
                </div>
              ) : null}
            </div>
          </Alert>
        ) : null}
        <Card className="!p-0 border-gray-200 shadow-sm">
          <div className="px-3 py-2 sm:px-4 sm:py-2">
            {/* Top row */}
            <div className="flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                  Date
                </span>
                <div className="min-w-[260px] max-w-full">
                  <DateRangePicker
                    startDate={dateRange?.startDate}
                    endDate={dateRange?.endDate}
                    onChange={(range) => {
                      const [start, end] = Array.isArray(range) ? range : [];
                      setDateRange({ startDate: start || null, endDate: end || null });
                    }}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                  Field
                </span>
                <div className="min-w-[240px] max-w-full">
                  <Select
                    id="leg-audit-date-field"
                    value={
                      DATE_FIELD_OPTIONS.find((opt) => opt.value === dateField)?.label || ""
                    }
                    onChange={(opt) => setDateField(opt?.value)}
                    options={DATE_FIELD_OPTIONS}
                    size="sm"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 lg:ml-auto">
                <Checkbox
                  id="onlyDuplicates"
                  checked={onlyDuplicates}
                  onChange={(target) => setOnlyDuplicates(!!target.checked)}
                  label="2+ legs"
                />
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                    Min
                  </span>
                  <Input
                    id="leg-audit-min-legs"
                    type="number"
                    min={1}
                    value={String(minLegCount)}
                    onChange={(e) => setMinLegCount(e?.target?.value)}
                    className="!p-2 w-[84px]"
                  />
                </div>
              </div>
            </div>

            {/* Bottom row */}
            <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2">
              <Checkbox
                id="excludeLocationNames"
                checked={useExcludeLocationSubstrings}
                onChange={(target) => setUseExcludeLocationSubstrings(!!target.checked)}
                label="Exclude location contains"
              />
              <div className="flex-1 min-w-0 max-w-2xl">
                <Input
                  id="leg-audit-exclude-subs"
                  type="text"
                  value={excludeLocationSubstrings}
                  onChange={(e) => setExcludeLocationSubstrings(e?.target?.value || "")}
                  placeholder="CANTEX YARD, …"
                  disabled={!useExcludeLocationSubstrings}
                  className="!p-2"
                />
              </div>
            </div>
          </div>
        </Card>

        {summaryError ? (
          <Alert type="error" title="Failed to load audit summary">
            {summaryError?.data?.message ||
              summaryError?.error ||
              "Unknown error"}
          </Alert>
        ) : null}

        <Card>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
            <div>
              <div className="text-sm font-semibold">
                Route groups ({filteredSummaryRows.length} shown
                {summaryRows.length > filteredSummaryRows.length
                  ? ` of ${summaryRows.length} from API`
                  : ""}
                )
              </div>
              <div className="text-xs text-gray-500">
                Sorted by leg count. Use the search box to filter this list. API cap: 500 groups.
              </div>
            </div>
          </div>

          <div className="overflow-x-auto max-h-[min(70vh,720px)] overflow-y-auto border border-gray-100 rounded-lg">
            <Table
              data={filteredSummaryRows}
              columns={summaryColumns}
              loading={isFetchingSummary}
              pageSize={summaryTablePageSize}
              hideFooter
              search
              stickyHeaders
              compact
            />
          </div>
        </Card>
      </div>

      <Modal
        visible={showDetails}
        onClose={() => setShowDetails(false)}
        title={
          <span className="line-clamp-2 text-left">
            Legs — {getRouteLabel(selectedRoute)}
          </span>
        }
        size="full"
        hideFooter
        contentWrapperClass="p-2 sm:p-4 space-y-3 flex flex-col min-h-0 max-h-[calc(100vh-4rem)]"
        panelClassName="max-w-[100vw] max-h-[calc(100vh-2rem)]"
      >
        {detailsState?.error ? (
          <Alert type="error" title="Failed to load legs">
            {detailsState?.error?.data?.message ||
              detailsState?.error?.error ||
              "Unknown error"}
          </Alert>
        ) : null}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between shrink-0">
          <div className="text-sm text-gray-600 space-y-0.5">
            <div>
              Showing <span className="font-medium text-gray-900">{displayedLegs.length}</span>{" "}
              row{displayedLegs.length === 1 ? "" : "s"}
              {showOnlyMissingContext && legs.length !== displayedLegs.length
                ? ` (filtered from ${legs.length} API rows)`
                : null}
            </div>
            <div className="text-xs text-gray-500">
              Updated rows stay visible in this report view, even after refresh.
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <Checkbox
              id="onlyMissingContext"
              checked={showOnlyMissingContext}
              onChange={(target) => setShowOnlyMissingContext(!!target.checked)}
              label="Only legs missing Manifest or Invoice"
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (!selectedRoute?.routeId) return;
                const request = {
                  routeId: normalizeId(selectedRoute.routeId),
                  start: summaryParams?.start,
                  end: summaryParams?.end,
                  dateField,
                  limit: 2000,
                };
                setLastDetailsRequest(request);
                triggerDetails(request);
              }}
              loading={detailsState?.isFetching}
            >
              Refresh legs
            </Button>
          </div>
        </div>

        {!detailsState?.isFetching && !detailsState?.error && displayedLegs.length === 0 ? (
          <Alert type="info" title="No legs returned for this route in the selected window">
            <div className="text-xs text-gray-600 space-y-1">
              <div>
                Try widening the date range or switching the date field (e.g. `dateCreated` vs
                `dateUpdated`).
              </div>
              {lastDetailsRequest ? (
                <pre className="bg-gray-50 border border-gray-200 rounded p-2 overflow-auto text-left text-[11px]">
                  {JSON.stringify(lastDetailsRequest, null, 2)}
                </pre>
              ) : null}
            </div>
          </Alert>
        ) : null}

        <div className="flex-1 min-h-0 flex flex-col border border-gray-100 rounded-lg overflow-hidden">
          <div className="overflow-x-auto overflow-y-auto flex-1 min-h-[240px] max-h-[min(70vh,720px)]">
            <Table
              data={displayedLegs}
              columns={legColumns}
              loading={detailsState?.isFetching}
              pageSize={detailsTablePageSize}
              hideFooter
              search
              stickyHeaders
              compact
            />
          </div>
        </div>
      </Modal>

      <Modal
        visible={!!editingLeg}
        onClose={closeEditModal}
        title="Update leg"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" onClick={closeEditModal}>
              Cancel
            </Button>
            <Button onClick={handleSaveLegUpdate} loading={isUpdatingLeg}>
              Save changes
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
            <div>
              Leg order #:{" "}
              <span className="font-medium text-gray-900">
                {getLegOrderNumberLabel(editingLeg)}
              </span>
            </div>
            <div className="mt-1">
              Job:{" "}
              <span className="font-medium text-gray-900">
                {getJobHumanLabel(editingLeg?.jobId)}
              </span>
            </div>
          </div>
          <div className="text-xs text-gray-500">
            Only changed fields are sent to the API.
          </div>
          <LocationDropdown
            id="audit-leg-origin"
            label="Origin"
            size="sm"
            clientId={normalizeId(editingLeg?.client?._id || editingLeg?.client)}
            value={
              editForm?.origin?.address?.fullAddress ||
              editForm?.origin?.name ||
              ""
            }
            onChange={(location) =>
              setEditForm((prev) => ({
                ...prev,
                origin: location,
              }))
            }
          />
          <LocationDropdown
            id="audit-leg-destination"
            label="Destination"
            size="sm"
            clientId={normalizeId(editingLeg?.client?._id || editingLeg?.client)}
            value={
              editForm?.destination?.address?.fullAddress ||
              editForm?.destination?.name ||
              ""
            }
            onChange={(location) =>
              setEditForm((prev) => ({
                ...prev,
                destination: location,
              }))
            }
          />
          <Input
            id="audit-leg-mileage-used"
            type="number"
            min={0}
            step="0.01"
            label="Mileage used"
            value={editForm?.mileageUsed}
            onChange={(inputTargetOrValue) =>
              setEditForm((prev) => ({
                ...prev,
                mileageUsed:
                  typeof inputTargetOrValue === "string"
                    ? inputTargetOrValue
                    : inputTargetOrValue?.value ?? "",
              }))
            }
          />
          {isCalculatingDistance ? (
            <div className="text-xs text-gray-500">
              Updating mileage from selected route...
            </div>
          ) : null}
        </div>
      </Modal>

      <Modal
        visible={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        title="Save leg route audit report"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowSaveModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveAuditReport} loading={isSavingReport}>
              Save
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <Input
            id="save-leg-audit-report-name"
            label="Report name"
            value={saveDraft?.name}
            onChange={(target) =>
              setSaveDraft((prev) => ({ ...prev, name: target?.value || "" }))
            }
          />
          <Input
            id="save-leg-audit-report-description"
            label="Description"
            value={saveDraft?.description}
            onChange={(target) =>
              setSaveDraft((prev) => ({
                ...prev,
                description: target?.value || "",
              }))
            }
          />
        </div>
      </Modal>
    </div>
  );

  if (embedded) {
    return (
      <div className="space-y-3">
        <Card>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-lg font-semibold">Leg Route Audit</div>
              <div className="text-xs text-gray-500">
                Review legs by route within a date window (incident validation).
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setSaveDraft({
                    name: `Leg Route Audit ${format(new Date(), "yyyy-MM-dd HH:mm")}`,
                    description: "",
                  });
                  setShowSaveModal(true);
                }}
              >
                Save report
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  refetchSummary();
                }}
                loading={isFetchingSummary}
              >
                Refresh
              </Button>
            </div>
          </div>
        </Card>
        {renderAuditContent(null)}
      </div>
    );
  }

  return (
    <SidebarContainer header={header} className="w-full">
      {({ parentHeight }) => renderAuditContent(parentHeight)}
    </SidebarContainer>
  );
};

export default LegRouteAudit;

